import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, ClipboardList, FileEdit, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/core/ui/Button'
import { useIsAuthenticated } from '@/core/auth/SessionStore'
import { useParteDiariaRecusadas } from '@/core/db/daos'
import type { DraftParteDiaria, OutParteDiaria } from '@/core/db/entities'
import { useParteDiariaStore } from '@/features/parteDiaria/parteDiariaStore'
import { deleteDraft, listDrafts, loadDraft } from '@/features/parteDiaria/draftParteDiariaRepo'
import { descartarParteOutbox, reabrirParteRecusada } from '@/features/parteDiaria/parteDiariaRepo'
import { dataBR } from '@/features/parteDiaria/parteDiariaRegras'

/**
 * Index da Parte Diária: recusadas pelo servidor (reabrir/descartar), rascunhos
 * em andamento e "Nova parte diária". Mesmo desenho do índice do carreteiro.
 */
export function ParteDiariaIndexRoute() {
  const authed = useIsAuthenticated()
  const navigate = useNavigate()
  const store = useParteDiariaStore()
  const recusadas = useParteDiariaRecusadas()
  const [drafts, setDrafts] = useState<DraftParteDiaria[] | null>(null)

  useEffect(() => {
    let cancelled = false
    listDrafts().then((d) => {
      if (!cancelled) setDrafts(d)
    })
    return () => {
      cancelled = true
    }
  }, [])

  if (!authed) return <Navigate to="/login" replace />

  async function abrirDraft(d: DraftParteDiaria) {
    const state = await loadDraft(d.id)
    if (!state) {
      await deleteDraft(d.id)
      setDrafts((all) => (all ? all.filter((x) => x.id !== d.id) : all))
      return
    }
    store.loadFromState(state)
    // Rascunho de outro dia: a data é fixa em "hoje" — reabre no passo 0 para recarregar o contexto.
    navigate(state.mveId != null && state.contexto != null ? '/parte-diaria/step1' : '/parte-diaria/step0')
  }

  function nova() {
    store.reset()
    navigate('/parte-diaria/step0')
  }

  async function removerDraft(id: string) {
    if (!confirm('Descartar esta parte diária em andamento?')) return
    await deleteDraft(id)
    setDrafts((all) => (all ? all.filter((x) => x.id !== id) : all))
    if (store.id === id) store.reset()
  }

  async function abrirRecusada(f: OutParteDiaria) {
    if (!f.rejeicao?.corrigivel) {
      await descartarRecusada(f)
      return
    }
    const state = await reabrirParteRecusada(f.id)
    if (!state) {
      alert('Não foi possível reabrir para correção. Só resta descartar.')
      return
    }
    store.loadFromState(state)
    navigate('/parte-diaria/step0')
  }

  async function descartarRecusada(f: OutParteDiaria) {
    const motivo = f.rejeicao?.message ?? f.failureReason ?? 'recusada pelo servidor'
    if (!confirm(`Descartar esta parte diária?\n\n${motivo}`)) return
    await descartarParteOutbox(f.id)
  }

  const hasDrafts = drafts != null && drafts.length > 0
  const hasRecusadas = recusadas != null && recusadas.length > 0

  return (
    <main className="min-h-svh bg-bg-base flex flex-col">
      <header className="sticky top-0 z-10 bg-bg-base/95 backdrop-blur border-b border-border pt-safe pl-safe pr-safe">
        <div className="flex items-center gap-2 px-2 py-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')} aria-label="Voltar">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-on-surface">Parte Diária</h1>
            <p className="text-xs text-on-surface-dim">
              {hasDrafts ? `${drafts!.length} em andamento` : 'Máquinas, veículos e equipamentos'}
            </p>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto pb-32">
        <div className="p-4 max-w-2xl mx-auto space-y-3">
          {hasRecusadas && (
            <section className="space-y-3">
              <h2 className="text-xs font-bold text-bad uppercase px-1">Recusadas pelo servidor ({recusadas!.length})</h2>
              {recusadas!.map((f) => (
                <RecusadaCard key={f.id} item={f} onOpen={abrirRecusada} onDelete={descartarRecusada} />
              ))}
              <h2 className="text-xs font-bold text-on-surface-dim uppercase px-1 pt-2">Em andamento</h2>
            </section>
          )}

          {drafts == null ? (
            <p className="text-sm text-on-surface-dim text-center py-8">Carregando…</p>
          ) : hasDrafts ? (
            drafts.map((d) => <DraftCard key={d.id} draft={d} onOpen={abrirDraft} onDelete={removerDraft} />)
          ) : (
            <div className="card-surface-variant px-4 py-8 text-center">
              <ClipboardList className="h-10 w-10 text-on-surface-dim mx-auto" />
              <p className="text-on-surface font-medium mt-3">Nenhuma parte diária em andamento</p>
              <p className="text-sm text-on-surface-dim mt-1">
                Toque em <span className="font-bold text-accent">+ Nova parte diária</span> para lançar o dia de hoje.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-bg-base/95 backdrop-blur border-t border-border p-4 pb-safe-plus-4 pl-safe pr-safe">
        <Button variant="primary" size="lg" onClick={nova} className="w-full max-w-2xl mx-auto flex items-center gap-2">
          <Plus className="h-5 w-5" />
          Nova parte diária
        </Button>
      </div>
    </main>
  )
}

function RecusadaCard({
  item,
  onOpen,
  onDelete,
}: {
  item: OutParteDiaria
  onOpen: (f: OutParteDiaria) => void
  onDelete: (f: OutParteDiaria) => void
}) {
  const corrigivel = item.rejeicao?.corrigivel ?? false
  return (
    <div className="card-surface border border-bad/60 flex items-stretch overflow-hidden">
      <button type="button" onClick={() => onOpen(item)} className="flex-1 p-4 text-left min-w-0">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-bad shrink-0" />
          <p className="text-on-surface font-medium truncate">{item.mvePrefixo ?? '(sem equipamento)'}</p>
          <span className="text-[10px] uppercase tracking-wide bg-bad/15 text-bad px-1.5 py-0.5 rounded ml-auto shrink-0">
            {item.modulo}
          </span>
        </div>
        <p className="text-xs text-on-surface-dim mt-1 truncate">
          {item.modulo === 'MVE'
            ? `${dataBR(item.dt)} · ${item.mve?.linhas.length ?? 0} linha(s)`
            : `${item.epp ? `${dataBR(item.epp.dtInicial)} a ${dataBR(item.epp.dtFinal)}` : dataBR(item.dt)}`}
        </p>
        <p className="text-sm text-on-surface mt-2">{item.rejeicao?.message ?? item.failureReason ?? 'Recusada pelo servidor'}</p>
        <p className={`text-xs mt-1 ${corrigivel ? 'text-accent' : 'text-on-surface-dim'}`}>
          {corrigivel ? 'Toque para corrigir' : 'Sem correção pelo app — toque para descartar'}
        </p>
      </button>
      <button type="button" onClick={() => onDelete(item)} className="px-3 text-bad hover:bg-bad/10 transition-colors flex items-center" aria-label="Descartar">
        <Trash2 className="h-5 w-5" />
      </button>
    </div>
  )
}

function DraftCard({
  draft,
  onOpen,
  onDelete,
}: {
  draft: DraftParteDiaria
  onOpen: (d: DraftParteDiaria) => void
  onDelete: (id: string) => void
}) {
  return (
    <div className="card-surface flex items-stretch overflow-hidden">
      <button type="button" onClick={() => onOpen(draft)} className="flex-1 p-4 text-left min-w-0">
        <div className="flex items-center gap-2">
          <FileEdit className="h-4 w-4 text-accent shrink-0" />
          <p className="text-on-surface font-medium truncate">{draft.mvePrefixo ?? '(sem equipamento)'}</p>
          {draft.modulo && (
            <span className="text-[10px] uppercase tracking-wide bg-accent/15 text-accent px-1.5 py-0.5 rounded ml-auto shrink-0">
              {draft.modulo}
            </span>
          )}
        </div>
        <p className="text-xs text-on-surface-dim mt-1">
          {dataBR(draft.dt)} · {draft.linhasCount} {draft.modulo === 'EPP' ? 'período' : 'linha(s)'} ·{' '}
          <span>{formatRelative(draft.lastUpdatedAt)}</span>
        </p>
      </button>
      <button type="button" onClick={() => onDelete(draft.id)} className="px-3 text-bad hover:bg-bad/10 transition-colors flex items-center" aria-label="Descartar">
        <Trash2 className="h-5 w-5" />
      </button>
    </div>
  )
}

function formatRelative(epochMs: number): string {
  const diff = Date.now() - epochMs
  const min = Math.floor(diff / 60_000)
  if (min < 1) return 'agora'
  if (min < 60) return `há ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `há ${h}h`
  return `há ${Math.floor(h / 24)}d`
}
